import Categories from 'ai/constants/availabilityPlacements';
import { AB_FEATURES_FLAGS } from 'ai/constants/superchat';
import { getGENAIAnonymousId, setGENAIAnonymousId } from 'ai/utils/sessionStorage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import UUIDv4 from 'utils/UUID';
import languageLocaleUtils from 'utils/LanguageLocale';
const { getCurrentLanguage } = languageLocaleUtils;

function hasCategoryId(obj, availableCategories) {
    let current = obj;

    while (current) {
        if (availableCategories.some(category => category.id === current.categoryId)) {
            return true;
        }

        current = current.parentCategory;
    }

    return false;
}

function getAllAvailableCategories() {
    const { isGenAIBathAndBodyChatEnabled, isGenAIToolsAndBrushesChatEnabled } = Sephora.configurationSettings;

    const CATEGORIES_AVAILABLE = [
        ...Categories.GENAI_CATEGORIES_HAIR,
        ...Categories.GENAI_CATEGORIES_SKINCARE,
        ...Categories.GENAI_CATEGORIES_MAKEUP
    ];

    if (isGenAIBathAndBodyChatEnabled) {
        CATEGORIES_AVAILABLE.push(...Categories.GENAI_CATEGORIES_BATH_AND_BODY);
    }

    if (isGenAIToolsAndBrushesChatEnabled) {
        CATEGORIES_AVAILABLE.push(...Categories.GENAI_CATEGORIES_TOOLS_AND_BRUSHES);
    }

    return CATEGORIES_AVAILABLE;
}

function getAllAvailableSearchTerms(featuresAIBeautyChat) {
    const { isGenAIBathAndBodyChatEnabled, isGenAIFragranceChatEnabled } = Sephora.configurationSettings;
    const currentLanguage = getCurrentLanguage();

    let SEARCH_TERMS_AVAILABLE;

    if (currentLanguage === 'FR') {
        SEARCH_TERMS_AVAILABLE = [...Categories.GENAI_TERMS_HAIR_FR, ...Categories.GENAI_TERMS_SKINCARE_FR, ...Categories.GENAI_TERMS_MAKEUP_FR];

        if (isGenAIBathAndBodyChatEnabled) {
            SEARCH_TERMS_AVAILABLE.push(...Categories.GENAI_TERMS_BATH_BODY_FR);
        }

        if (isGenAIFragranceChatEnabled && featuresAIBeautyChat && featuresAIBeautyChat?.includes(AB_FEATURES_FLAGS.FRAGRANCE)) {
            SEARCH_TERMS_AVAILABLE.push(...Categories.GENAI_TERMS_FRAGRANCE_FR);
        }
    } else {
        SEARCH_TERMS_AVAILABLE = [...Categories.GENAI_TERMS_HAIR, ...Categories.GENAI_TERMS_SKINCARE, ...Categories.GENAI_TERMS_MAKEUP];

        if (isGenAIBathAndBodyChatEnabled) {
            SEARCH_TERMS_AVAILABLE.push(...Categories.GENAI_TERMS_BATH_BODY);
        }

        if (isGenAIFragranceChatEnabled && featuresAIBeautyChat && featuresAIBeautyChat?.includes(AB_FEATURES_FLAGS.FRAGRANCE)) {
            SEARCH_TERMS_AVAILABLE.push(...Categories.GENAI_TERMS_FRAGRANCE);
        }
    }

    return SEARCH_TERMS_AVAILABLE;
}

function isAIBeautyChatCategoryFragrance(categoryId) {
    const { isGenAIChatPLPEnabled, isGenAIFragranceChatEnabled } = Sephora.configurationSettings;

    if (!categoryId || !isGenAIFragranceChatEnabled || !isGenAIChatPLPEnabled) {
        return false;
    }

    const AVAILABLE_CATEGORIES = Categories.GENAI_CATEGORIES_FRAGRANCE;

    return AVAILABLE_CATEGORIES.some(category => category.id === categoryId);
}

function isAIBeautyChatCategoryGift(categoryId) {
    const { isGenAIChatPLPEnabled, genAIGiftFinderV2 } = Sephora.configurationSettings;

    if (!categoryId || !genAIGiftFinderV2?.isEnabled || !isGenAIChatPLPEnabled) {
        return false;
    }

    const AVAILABLE_CATEGORIES = Categories.GENAI_CATEGORIES_GIFT;

    return AVAILABLE_CATEGORIES.some(category => category.id === categoryId);
}

function isAIBeautyChatEnabledPDP(product, showAIBeautyChat, featuresAIBeautyChat) {
    if (!product || !product.parentCategory) {
        return false;
    }

    if (!Sephora.configurationSettings?.isGenAIChatPDPEnabled) {
        return false;
    }

    const AVAILABLE_CATEGORIES = getAllAvailableCategories();
    let isGenAIChatPDPEnabled = hasCategoryId(product.parentCategory, AVAILABLE_CATEGORIES) && showAIBeautyChat;

    if (!isGenAIChatPDPEnabled && featuresAIBeautyChat) {
        const { isGenAIFragranceChatEnabled, genAIGiftFinderV2 } = Sephora.configurationSettings;

        if (isGenAIFragranceChatEnabled && hasCategoryId(product.parentCategory, Categories.GENAI_CATEGORIES_FRAGRANCE)) {
            Sephora.logger.verbose('SuperChat: Fragrance category detected');
            isGenAIChatPDPEnabled = featuresAIBeautyChat?.includes(AB_FEATURES_FLAGS.FRAGRANCE);
        }

        if (genAIGiftFinderV2?.isEnabled && hasCategoryId(product.parentCategory, Categories.GENAI_CATEGORIES_GIFT)) {
            Sephora.logger.verbose('SuperChat: Gift category detected');
            isGenAIChatPDPEnabled = featuresAIBeautyChat?.includes(AB_FEATURES_FLAGS.GIFTS);
        }
    }

    return isGenAIChatPDPEnabled;
}

function isAIBeautyChatGiftFinderEnabledPDP(product) {
    if (!product || !product.parentCategory) {
        return false;
    }

    const AVAILABLE_CATEGORIES = Categories.GENAI_CATEGORIES_GIFT;

    if (hasCategoryId(product.parentCategory, AVAILABLE_CATEGORIES)) {
        return true;
    }

    return false;
}

function isAIBeautyChatEnabledPLP(categoryId) {
    if (!categoryId) {
        return false;
    }

    const { isGenAIChatPLPEnabled } = Sephora.configurationSettings;

    const AVAILABLE_CATEGORIES = getAllAvailableCategories();

    const enablePLP = AVAILABLE_CATEGORIES.some(category => category.id === categoryId);

    if (enablePLP && isGenAIChatPLPEnabled) {
        return true;
    }

    return false;
}

function isAIBeautyChatEnabledSRP(pageType, searchTerm, featuresAIBeautyChat) {
    if (pageType !== 'searchResults') {
        return false;
    }

    if (!searchTerm || searchTerm.trim() === '') {
        return false;
    }

    const { isGenAIChatSRPEnabled } = Sephora.configurationSettings;
    const SEARCH_TERMS_AVAILABLE = getAllAvailableSearchTerms(featuresAIBeautyChat);
    const currentLanguage = getCurrentLanguage();

    let isTermFound = false;

    if (currentLanguage === 'FR') {
        isTermFound = SEARCH_TERMS_AVAILABLE.some(term => term.localeCompare(searchTerm, 'fr', { sensitivity: 'base' }) === 0);
    } else {
        isTermFound = SEARCH_TERMS_AVAILABLE.includes(searchTerm);
    }

    if (isTermFound && isGenAIChatSRPEnabled) {
        return true;
    }

    return false;
}

function getUUID() {
    const token = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);
    let uuid;

    if (token) {
        const tokenPayload = token.split('.')[1];
        const userTokenData = JSON.parse(atob(tokenPayload.replace(/-/g, '+').replace(/_/g, '/')));

        if (userTokenData?.AuthData?.uuid) {
            uuid = JSON.stringify(userTokenData?.AuthData?.uuid).replace(/\"/g, '');
        }
    }

    return uuid;
}

function getAnonymousId() {
    const storedAnonymousId = getGENAIAnonymousId();

    if (storedAnonymousId) {
        return storedAnonymousId;
    }

    const uuid = getUUID();

    if (uuid) {
        setGENAIAnonymousId(uuid);

        return uuid;
    }

    const generatedUUID = UUIDv4();
    setGENAIAnonymousId(generatedUUID);

    return generatedUUID;
}

function isAIBeautyGiftEnabled(categoryId) {
    if (!categoryId) {
        return false;
    }

    const AVAILABLE_CATEGORIES = Categories.GENAI_CATEGORIES_GIFT;

    const enablePLP = AVAILABLE_CATEGORIES.some(category => category.id === categoryId);

    return enablePLP;
}

export {
    isAIBeautyChatEnabledPDP,
    isAIBeautyChatEnabledPLP,
    isAIBeautyChatEnabledSRP,
    getUUID,
    getAnonymousId,
    isAIBeautyGiftEnabled,
    isAIBeautyChatGiftFinderEnabledPDP,
    isAIBeautyChatCategoryFragrance,
    isAIBeautyChatCategoryGift
};
